import { Component, inject, Input, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  HOME_ROUTE,
  INVESTMENT_LIST_ROUTE,
  PORTFO,
  PURCHASE_ROUTE,
  RESULT_ROUTE,
  TRANSACTIONS_ROUTE,
} from '../../../data-access/constants/app-routes';
import { ErrorService } from '../../../components/core/services/error.service';
import { EIntrackEventName } from '../../../components/core/models/intrack-event-name.enum';
import { WealthNavigationService } from '@client-monorepo/wealth/navigation';
import { NgxButtonComponent } from '@digipay/ngx-button';
import { getInstrumentName } from './models/instrument-name';
import { IDynamicButton } from '../../../data-access/models/dynamic-button.interface';
import { NgxEventTrackerService } from '@digipay/ngx-event-tracker';
import { NgClass } from '@angular/common';

const RESULT_PAGE_KEY = '__RP__';

@Component({
  selector: 'app-operation-result',
  templateUrl: './operation-result.component.html',
  styleUrls: ['./operation-result.component.scss'],
  standalone: true,
  imports: [NgxButtonComponent, NgClass],
})
export class OperationResultComponent implements OnInit {
  @Input() assetName = '';

  titleText = 'دوباره تلاش کنید';
  status: 'Succeed' | 'Failed' = 'Failed';
  action: 'sell' | 'investment' | 'cashout' | 'cashin' = 'investment';
  incomplete = true;
  symbol!: string;
  instrumentName!: string;
  receiptNumber!: string;
  description = '';
  image = '';
  navigationService = inject(WealthNavigationService);
  instrumentType: string;
  ipoDate: string;

  buttons: IDynamicButton[] = [
    {
      id: 'TRANSACTIONS',
      title: 'سرمایه‌گذاری جدید',
      style: 'tinted-on-elevated',
    },
    {
      id: 'GOT_IT',
      title: 'متوجه شدم',
      style: 'fill',
    },
  ];
  private eventService = inject(NgxEventTrackerService);

  constructor(
    private router: Router,
    private errorService: ErrorService,
    private activatedRoute: ActivatedRoute,
  ) {
    const params = this.activatedRoute.snapshot.queryParams;
    this.status = params['status'];
    this.action = params['action'];
    this.receiptNumber = params['receiptNumber'];
    this.incomplete = params['incomplete'];
    this.symbol = params['symbol'];
    this.instrumentType = params['type'];
    this.ipoDate = params['ipoDate']?.replaceAll('-', '/');
    this.instrumentName = getInstrumentName(this.symbol);

    this.setParams();
  }

  ngOnInit(): void {
    if (this.errorService.isErrorPageExpired()) {
      this.navigationService.navigate([PURCHASE_ROUTE, this.symbol]);
    }
  }

  private setRouteInLocalstorage() {
    const TEN_DAYS_IN_MS = 10 * 24 * 60 * 60 * 1000;
    const now = new Date();

    const newRoute = {
      value: this.router.url,
      expiry: now.getTime() + TEN_DAYS_IN_MS,
    };

    const storedRoutes = localStorage.getItem(RESULT_PAGE_KEY);
    const routes: Array<{ value: string; expiry: number }> = storedRoutes ? JSON.parse(storedRoutes) : [];

    routes.push(newRoute);
    localStorage.setItem(RESULT_PAGE_KEY, JSON.stringify(routes));
  }

  private isRouteExpired(): string[] | null {
    const storedRoutes = localStorage.getItem(RESULT_PAGE_KEY);
    if (!storedRoutes) {
      this.setRouteInLocalstorage();
      return null;
    }
    let routes: Array<{ value: string; expiry: number }>;
    try {
      routes = JSON.parse(storedRoutes);
    } catch (error) {
      localStorage.removeItem(RESULT_PAGE_KEY);
      return null;
    }
    const existRoute = routes.find((route) => route.value === this.router.url);
    if (!existRoute) {
      this.setRouteInLocalstorage();
      return null;
    }
    const now = new Date().getTime();
    const validRoutes = routes.filter((route) => route.expiry > now);

    if (validRoutes.length !== routes.length) {
      if (validRoutes.length === 0) {
        localStorage.removeItem(RESULT_PAGE_KEY);
        this.setRouteInLocalstorage();
        return null;
      } else {
        localStorage.setItem(RESULT_PAGE_KEY, JSON.stringify(validRoutes));
      }
    }

    return validRoutes.map((route) => route.value);
  }

  onBackClicked() {
    if (this.incomplete) {
      this.errorService.setErrorPageExpired(false);
      this.navigationService
        .navigate([RESULT_ROUTE], {
          queryParams: {
            symbol: this.symbol,
            expired: true,
          },
        })
        .then(() => {
          this.navigationService.navigate([this.action === 'sell' ? INVESTMENT_LIST_ROUTE : PURCHASE_ROUTE, this.symbol]);
        });
    } else {
      this.navigationService.navigate([HOME_ROUTE]);
    }
  }

  setParams() {
    if (this.incomplete) {
      this.image = 'wealth-assets/svg/failed-result.svg';
      this.titleText = 'درخواست شما کامل نشد';
      this.description = 'درخواست سرمایه‌گذاری شما کامل نشد. لطفا دوباره تلاش کنید.';

      return;
    }
    if (this.status === 'Succeed') {
      if (this.ipoDate) {
        this.description = `سرمایه‌گذاری شما در عرضه اولیه ${this.instrumentName} در روز ${this.ipoDate.split(' ')[0]} به صورت خودکار انجام خواهد شد.
برای حذف درخواست به صفحه اطلاعات عرضه اولیه بروید.`;
        this.titleText = 'درخواست شما ثبت شد';
        this.image = 'wealth-assets/svg/success-result.svg';
        this.sendEvent();
      } else {
        switch (this.action) {
          case 'sell':
            this.description = this.symbol?.startsWith('IRT')
              ? `نهایی شدن فروش واحد‌های صندوق ${this.instrumentName}،  یک روز کاری زمان می‌برد. مبلغ حاصل از فروش به کیف پول ETF شما واریز خواهد شد.`
              : this.instrumentType == 'CrowdFund'
                ? ''
                : `نهایی شدن فروش واحد‌های صندوق ${this.instrumentName}، ۱ تا ۲ روز کاری زمان می‌برد.`;
            this.titleText = 'درخواست فروش با موفقیت ارسال شد ';
            this.image = 'wealth-assets/svg/success-result.svg';
            break;

          case 'cashout':
            this.description =
              'نهایی شدن برداشت حداکثر ۲ روز کاری زمان می‌برد. می‌توانید وضعیت سفارش خود را در قسمت سفارش‌های در انتظار مشاهده کنید.';
            this.titleText = 'درخواست برداشت از کیف پول با موفقیت ثبت شد. ';
            this.image = 'wealth-assets/svg/cashout-successful.svg';
            break;
          case 'cashin':
            this.description = '';
            this.titleText = 'درخواست افزایش موجودی کیف پول با موفقیت ثبت شد.';
            this.image = 'wealth-assets/svg/sell-successful.svg';
            break;
          default:
            this.description =
              this.instrumentType == 'CrowdFund'
                ? ''
                : `نهایی شدن سرمایه‌گذاری شما در صندوق ${this.instrumentName}، ۱ تا ۲ روز کاری زمان می‌برد.`;
            this.titleText = 'درخواست سرمایه‌گذاری با موفقیت ارسال شد ';
            this.image = 'wealth-assets/svg/success-result.svg';
            this.sendEvent();
            break;
        }
      }
    } else {
      switch (this.action) {
        case 'sell':
          this.description = 'درخواست فروش واحد‌های صندوق، انجام نشد. لطفا دقایقی بعد دوباره تلاش کنید.';
          this.image = 'wealth-assets/svg/failed-result.svg';
          break;
        case 'cashout':
          this.description = 'درخواست برداشت از کیف پول ثبت نشد. لطفا دقایقی بعد دوباره تلاش کنید.';
          this.image = 'wealth-assets/svg/cashout-failed.svg';
          break;
        case 'cashin':
          this.description = 'درخواست افزایش موجودی کیف پول ثبت نشد. لطفا دقایقی بعد دوباره تلاش کنید.';
          this.image = 'wealth-assets/svg/sell-failed.svg';
          break;

        default:
          this.description =
            this.instrumentType == 'CrowdFund'
              ? ' لطفا دقایقی بعد دوباره تلاش کنید.'
              : 'درخواست سرمایه‌گذاری شما ارسال نشد. لطفا دقایقی بعد دوباره تلاش کنید.';
          this.image = 'wealth-assets/svg/failed-result.svg';
          break;
      }
      this.titleText = 'دوباره تلاش کنید';
    }
  }

  private sendEvent() {
    if (this.isRouteExpired()) return;
    const eventData = {
      eventName: EIntrackEventName.PAYMENT_DONE,
      eventData: {
        Fund_Id: this.symbol,
        State: this.status,
        Order_Id: this.receiptNumber,
      },
    };
    this.eventService.sendEvent(eventData);
  }

  goToPortfo() {
    this.navigationService.navigate([PORTFO]);
  }

  actionHandler(id: string) {
    if (id === 'TRANSACTIONS') {
      this.navigationService.navigate([HOME_ROUTE]);
    } else {
      this.navigationService.navigate([TRANSACTIONS_ROUTE]);
    }
  }
}
